import { getDataRowsAndMeta } from 'api/customer/xlsxImportShared';
import { isEmptyRow } from './parsing';
import type { ImportAdapter, ImportRowAction, ImportRowPreview } from './types';

export interface AnalyzeRowsResult<TEntity> {
    rows: ImportRowPreview<TEntity>[];
    truncated: boolean;
}

/**
 * Pure row-analysis core used by ImportWizardDialog's review step: turns raw spreadsheet
 * rows into row previews via the adapter, then layers on same-file duplicate-identity
 * detection, unchanged-vs-update reclassification, and the default selection rule
 * (valid creates/updates selected, ambiguous/invalid rows blocked). Kept adapter-agnostic
 * and side-effect free so it can be unit tested without mounting the wizard component.
 */
export function analyzeImportRows<TEntity>(
    adapter: ImportAdapter<TEntity>,
    rawRows: unknown[][],
    mapping: Record<string, number | undefined>,
    assumeExtraTitleRow: boolean,
    currentData: TEntity[]
): AnalyzeRowsResult<TEntity> {
    // Keyed by column *label* (not internal key) so adapters can reuse the existing
    // getCell()/rowToPartial() helpers, which already index columns by their Hebrew header text.
    const cols: Record<string, number> = {};
    adapter.columns.forEach((c) => {
        const idx = mapping[c.key];
        if (idx != null) cols[c.label] = idx;
    });
    const { dataRows, firstDataSheetLine1Based } = getDataRowsAndMeta(rawRows, assumeExtraTitleRow);
    const maxLines = adapter.maxRows ?? 5000;
    const slice = dataRows.slice(0, maxLines);

    const seenIdentity = new Map<string, number>();
    const built: ImportRowPreview<TEntity>[] = [];

    slice.forEach((row, i) => {
        const lineNum = i + firstDataSheetLine1Based;
        if (isEmptyRow(row)) return;
        const analysis = adapter.analyzeRow(row, cols, { currentData });

        const identityKey =
            analysis.identity.idNum != null
                ? `id:${analysis.identity.idNum}`
                : analysis.identity.externalIdStr
                  ? `ext:${analysis.identity.externalIdStr}`
                  : analysis.identity.extraKey
                    ? `extra:${analysis.identity.extraKey}`
                    : undefined;

        let action: ImportRowAction = analysis.action;
        let duplicateOfLine: number | undefined;
        const errors = [...analysis.errors];
        if (identityKey) {
            const prevLine = seenIdentity.get(identityKey);
            if (prevLine != null) {
                duplicateOfLine = prevLine;
                action = 'duplicate';
                errors.push(`מזהה זה מופיע כבר בשורה ${prevLine} בקובץ; רק שורה אחת תבוצע כדי למנוע עדכון כפול לא מכוון`);
            } else {
                seenIdentity.set(identityKey, lineNum);
            }
        }

        const diffs = analysis.existing
            ? Object.entries(analysis.patch)
                  .map(([key, newValue]) => {
                      const oldValue = (analysis.existing as unknown as Record<string, unknown>)[key];
                      return {
                          key,
                          label: adapter.fieldLabels?.[key] ?? key,
                          oldValue,
                          newValue,
                          changed: String(oldValue ?? '') !== String(newValue ?? '')
                      };
                  })
                  .filter((d) => d.changed)
            : [];

        if (action === 'update' && diffs.length === 0) action = 'unchanged';

        built.push({
            rowIndex: i,
            line: lineNum,
            raw: row,
            action,
            identity: analysis.identity,
            label: analysis.label,
            existing: analysis.existing,
            patch: analysis.patch,
            diffs,
            errors,
            warnings: analysis.warnings,
            duplicateOfLine,
            selected: (action === 'create' || action === 'update') && errors.length === 0
        });
    });

    return { rows: built, truncated: dataRows.length > maxLines };
}
